import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { IJourneyResp, ISchedules } from '../../../../services/journey.service';
import {
  chooseCalendar,
  ChooseCalendarResult,
  EScheduleType,
  isReSchedule,
  saveCurrentSchedule,
  saveTypeOfAppointment,
  TypeOfAppointment
} from '../../../../features/calendar/calendar-slice';
import { EScheduleSteps, initizalizeScheduleAppointment } from '../../../../features/schedule/schedule-slice';
import { useRescheduleReason } from '../../../shared/Calendar/RescheduleReason/hook';
import { ScheduleAnalyticsTrackerInstance } from '../../../../lib/gtm';
import SchedulesModel from './model';

const PAST_PREVIEW_MAX = 3;

export default function SchedulesViewModel(journey: IJourneyResp) {
  const dispatch: AppDispatch = useDispatch();
  const { isOpen, openModalRescheduleReason, handleConfirm, handleCancel } = useRescheduleReason();
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setExpanded(false);
  }, [journey.journeyid]);

  const openCalendarReschedule = async (schedule: ISchedules) => {
    let localSchedule = { ...schedule };
    if (!localSchedule.rescheduleReason) {
      const result = await openModalRescheduleReason();
      if (result) {
        localSchedule = { ...localSchedule, rescheduleReason: result.reason };
      } else {
        return handleCancel();
      }
    }
    const scheduleType = localSchedule.returnSchedule ? EScheduleType.RETURN : EScheduleType.DEFAULT;
    const scheduleCalendar = journey.calendars?.find(calendar => calendar.id === localSchedule.calendarId);
    ScheduleAnalyticsTrackerInstance.clear();
    dispatch(saveCurrentSchedule(localSchedule));
    dispatch(isReSchedule(true));
    dispatch(chooseCalendar({ journey, schedule: localSchedule, scheduleType })).then(res => {
      const payload = res.payload as ChooseCalendarResult;
      if (!payload) return;
      if (scheduleCalendar?.type === 'EXAM') {
        dispatch(
          initizalizeScheduleAppointment({
            currentStep: EScheduleSteps.openCalendar
          })
        );
        return;
      }
      if (scheduleType === EScheduleType.RETURN) {
        const isFlexible =
          payload.availableList.length > 1 &&
          payload.availableList.some(calendar => calendar.type === TypeOfAppointment.VIRTUAL_CONSULTATION) &&
          payload.availableList.some(calendar => calendar.type === TypeOfAppointment.PHYSICAL_APPOINTMENT);

        if (isFlexible) {
          dispatch(initizalizeScheduleAppointment({ currentStep: EScheduleSteps.returnAppointment }));
        } else {
          const typeOfAppointment = payload.availableList[0].type;
          dispatch(saveTypeOfAppointment({ typeOfAppointment: typeOfAppointment as TypeOfAppointment }));
          dispatch(initizalizeScheduleAppointment({ currentStep: EScheduleSteps.openCalendar }));
        }
        return;
      }

      return dispatch(
        initizalizeScheduleAppointment({
          currentStep: EScheduleSteps.rescheduleAppointment
        })
      );
    });
  };

  const isPlaceholder = SchedulesModel.isPlaceholder(journey.schedules ?? []);
  const activeSchedules = useMemo(() => SchedulesModel.getActiveSchedules(journey.schedules), [journey.schedules]);
  const pastSchedules = useMemo(() => SchedulesModel.getPastSchedules(journey.schedules), [journey.schedules]);
  const previewPastSchedules = useMemo(
    () => SchedulesModel.getPreview(pastSchedules, PAST_PREVIEW_MAX),
    [pastSchedules]
  );
  const hasMore = !expanded && SchedulesModel.hasMore(pastSchedules, PAST_PREVIEW_MAX);
  const showMore = () => setExpanded(true);

  return {
    isPlaceholder,
    activeSchedules,
    pastSchedules: expanded ? pastSchedules : previewPastSchedules,
    hasMore,
    showMore,
    openCalendarReschedule,
    isOpen,
    handleConfirm,
    handleCancel
  };
}
